import { useMemo, type ComponentProps, type ComponentType } from 'react';
import { ArrowRightLeft, Bell } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';

import { NavMain } from './components/NavMain';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar';
import { NavUser } from './components/NavUser';
import { NavSecondary } from './components/NavSecondary';
import { useUserStore } from '@/stores/userStore';
import { getHasPermissionRoutes, routes } from '@/router/routes';
import type { HasMetaRouteWithFullPath } from '@/router';
import { mapRoutesWithFullPath } from '@/router/path';
import { LogoElement } from './components/LogoElement';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';

// 主导航菜单项类型
export interface NavMainItem {
  title: string;
  url: string;
  icon: ComponentType<{ className?: string }>;
  isActive?: boolean;
  items?: {
    title: string;
    url: string;
  }[];
}

// 次级导航菜单项类型
export interface NavSecondaryItem {
  title: string;
  url: string;
  icon: ComponentType<{ className?: string }>;
  meta: string;
}

export interface NavSecondaryMessageItem {
  title: string;
  icon: ComponentType<{ className?: string }>;
}

const navSecondary = [
  {
    title: '消息',
    url: '#',
    icon: Bell,
    meta: 'message',
  },
] as NavSecondaryItem[];

/**
 * 当前团队切换入口
 * 复用 Creative 的侧边栏团队切换交互，跳转到登录页中的团队选择表单
 */
function CompanyBar() {
  const navigate = useNavigate();
  const { userInfo } = useUserStore();
  const { state } = useSidebar();
  const isCollapsed = state === 'collapsed';
  const companyName = userInfo?.team?.companyName || '未选择团队';

  /**
   * 切换到团队选择页
   */
  const handleSwitch = () => {
    navigate('/login?pageType=team');
  };

  if (isCollapsed) {
    return (
      <div className="px-2 py-4">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon-sm" className="w-full" onClick={handleSwitch}>
              <ArrowRightLeft className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">{companyName}</TooltipContent>
        </Tooltip>
      </div>
    );
  }

  return (
    <div
      className="mx-3 mb-4 px-3 py-2 rounded-md border bg-muted/30 flex items-center gap-2 cursor-pointer hover:bg-muted/60 transition-colors"
      onClick={handleSwitch}
    >
      <span className="flex-1 text-sm truncate">{companyName}</span>
      <Button
        variant="ghost"
        size="icon-sm"
        className="shrink-0"
        onClick={(e) => {
          e.stopPropagation();
          handleSwitch();
        }}
      >
        <ArrowRightLeft className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

/** 组装当前用户的侧边栏菜单及团队入口 */
export default function AppSidebar({ ...props }: ComponentProps<typeof Sidebar>) {
  const location = useLocation();
  const navigate = useNavigate();
  const { userInfo } = useUserStore();
  const navMainRoutes = useMemo(() => {
    /** 短剧库管理仅隐藏菜单入口，路由访问仍沿用原权限 */
    const permissionRoutes = getHasPermissionRoutes(
      userInfo?.permissionCodes || [],
      routes[0].children || [],
      (route) =>
        route.permCode !== undefined &&
        route.path !== '' &&
        route.path !== 'playlet' &&
        !!route.meta
    );
    return mapRoutesWithFullPath(permissionRoutes) as HasMetaRouteWithFullPath[];
  }, [userInfo?.permissionCodes]);

  return (
    <Sidebar collapsible="compact" {...props}>
      {/* 侧边栏头部 */}
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              className="data-[slot=sidebar-menu-button]:px-2.5 pt-2 pb-1 h-9 text-nowrap"
            >
              <LogoElement />
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      {/* 侧边栏内容 */}
      <SidebarContent>
        <NavMain routes={navMainRoutes} location={location} navigate={navigate} />
        <NavSecondary items={navSecondary} className="mt-auto" />
      </SidebarContent>

      {/* 当前团队切换入口 */}
      <CompanyBar />

      {/* 侧边栏底部 */}
      <SidebarFooter>
        <NavUser />
      </SidebarFooter>
    </Sidebar>
  );
}
